import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import { ImCross } from 'react-icons/im';
import { Col, Row } from 'reactstrap';
import { object, string } from 'yup';
import { Button, Checkbox, Input, ModalComponent, Select } from '../../../common/components';
import { ACTION_EDIT, ACTION_NEW } from '../../../constants/gl-configuration';

const AccountNumberModal = ({
  isOpen,
  toggle,
  segmentList,
  entityList,
  segmentDropdownValues,
  fetchSegmentValue,
  action,
  selectedValue,
  onSubmit,
}) => {
  const checkDisable = (values) => {
    return (
      !values?.description ||
      !values?.entity?.length > 0 ||
      (action === ACTION_NEW && values?.an?.length !== segmentList?.length) ||
      (action === ACTION_EDIT &&
        values?.description === selectedValue?.description &&
        values?.entity.sort((a, b) => a - b).toString() ===
          selectedValue?.accountNumberTemplate
            ?.map((microItem) => microItem?.templateBusinessId)
            .sort((a, b) => a - b)
            .toString())
    );
  };

  const NewAccountNumberModal = () => {
    return (
      <div className="account-modal">
        <div className="close-btn">
          <ImCross color="#D38529" onClick={toggle} />
        </div>
        <div className="account-modal-header">{action} Account Number</div>
        <Formik
          initialValues={{
            an:
              selectedValue?.accountNumber
                ?.split('-')
                .map((item) => ({ label: item, value: item })) || [],
            entity:
              selectedValue?.accountNumberTemplate?.map(
                (microItem) => microItem?.templateBusinessId,
              ) || [],
            description: selectedValue?.description || '',
          }}
          validationSchema={object({
            description: string().trim().required(`Description is Required`),
          })}
          onSubmit={(values) => {
            const payload = {
              accountNumber: values?.an
                ?.map((item) => item?.value?.toString())
                .toString()
                .replace(/,/g, '-'),
              templateBusinessIds: values?.entity,
              description: values?.description,
            };
            if (action === ACTION_EDIT) {
              payload.accountNumberId = selectedValue?.accountNumberId;
              payload.accountNumber = selectedValue?.accountNumber;
            }
            onSubmit(payload);
          }}
        >
          {({ handleSubmit, setFieldValue, values }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <div className="account-sub-header">CHOOSE A SEGMENT</div>
                <Row className="mb-3 account-modal-row">
                  {segmentList?.map((item, index) => (
                    <Fragment key={item?.value}>
                      <Col
                        lg={segmentList?.length > 4 ? '3' : ''}
                        className=" account-number-input"
                      >
                        <div className="common-local-search w-100 account-segment">
                          <i className="fas fa-search" />
                          <Select
                            name="an"
                            classNamePrefix="select-dropdown"
                            placeholder={item?.label}
                            size="lg"
                            isClearable
                            cacheOptions
                            isAsync
                            value={values.an[index]}
                            defaultOptions={
                              action === ACTION_NEW ? segmentDropdownValues[index] : []
                            }
                            loadOptions={(v) => fetchSegmentValue(item?.value, v)}
                            required
                            onChange={(val) => {
                              const temp = [...values.an];
                              temp[index] = val;
                              if (
                                action === ACTION_NEW &&
                                values.an
                                  .map((subItem) => subItem.label.split(' - ')[1])
                                  .toString()
                                  .replace(/,/g, '-') === values?.description
                              ) {
                                setFieldValue(
                                  'description',
                                  temp
                                    .map((subItem) => subItem.label.split(' - ')[1])
                                    .toString()
                                    .replace(/,/g, '-')
                                    .slice(0, 255),
                                );
                              }
                              setFieldValue('an', temp);
                            }}
                            disabled={action === ACTION_EDIT}
                          />
                        </div>
                        {index !== segmentList?.length - 1 && (
                          <span className="segment-spacer">-</span>
                        )}
                      </Col>
                    </Fragment>
                  ))}
                </Row>
                <Row>
                  <Col>
                    <Input
                      formInput
                      name="description"
                      type="text"
                      value={values?.description}
                      className="account-number-description"
                      placeholder="Description"
                      maxLength={255}
                    />
                  </Col>
                </Row>
                <hr />
                <div className="account-modal-header">Choose Entity</div>
                <Row>
                  {entityList?.map((item) => {
                    return (
                      <Col lg="6" key={item?.accountEntityId}>
                        <div className="show-checkbox">
                          <Checkbox
                            label=""
                            formInput
                            simple
                            name="entity"
                            checked={values.entity.includes(item?.accountEntityId)}
                            onChange={() => {
                              const temp = [...(values.entity ?? [])];
                              if (!temp.includes(item?.accountEntityId)) {
                                temp.push(item?.accountEntityId);
                              } else {
                                const rIndex = temp.indexOf(item?.accountEntityId);
                                temp.splice(rIndex, 1);
                              }
                              setFieldValue('entity', temp);
                            }}
                          />
                          <p>{item?.name}</p>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
                <hr />
                <div className="account-modal-btns">
                  <Button
                    color="primary"
                    className="account-create-btn"
                    size="xs"
                    type="submit"
                    disabled={checkDisable(values)}
                  >
                    {action === ACTION_NEW ? 'Create' : 'Modify'}
                  </Button>
                  <Button className="account-cancel-btn" size="xs" onClick={toggle}>
                    Cancel
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  };
  return (
    <ModalComponent isOpen={isOpen} toggle={toggle} className="account-modal-wrapper">
      {NewAccountNumberModal()}
    </ModalComponent>
  );
};

AccountNumberModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  segmentList: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
    }),
  ).isRequired,
  entityList: PropTypes.arrayOf(
    PropTypes.shape({
      accountEntityId: PropTypes.number,
      name: PropTypes.string,
    }),
  ).isRequired,
  segmentDropdownValues: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      }),
    ),
  ),
  fetchSegmentValue: PropTypes.func,
  action: PropTypes.oneOf([ACTION_NEW, ACTION_EDIT]),
  selectedValue: PropTypes.shape({
    description: PropTypes.string,
    accountNumberTemplate: PropTypes.arrayOf(
      PropTypes.shape({
        accountNumberId: PropTypes.number,
        name: PropTypes.string,
        templateBusinessId: PropTypes.number,
      }),
    ),
    accountNumber: PropTypes.string,
    accountNumberId: PropTypes.number,
  }),
  onSubmit: PropTypes.func.isRequired,
};

AccountNumberModal.defaultProps = {
  action: ACTION_NEW,
  selectedValue: null,
  segmentDropdownValues: null,
  fetchSegmentValue: () => {},
};

export default AccountNumberModal;
